<template>
    <transition name="modal">
        <div class="modal-mask">
            <div class="modal-wrapper">
                <div class="modal-container">

                    <!-- HEADER -->
                    <div class="atts-modal__header">
                        <h5 class="atts-modal__title">
                            {{ target=='product' ? 'Crear nueva variante' : 'Asignar atributos' }}
                        </h5>
                        <button type="button" class="atts-modal__close" aria-label="Cerrar" @click="close()">
                            <i class="bi bi-x-lg"></i>
                        </button>
                    </div>

                    <!-- BODY -->
                    <div class="atts-modal__body">
                        <slot name="body">
                            <!-- BUSQUEDA -->
                            <div class="form-card__field mb-3">
                                <label class="form-card__label">Búsqueda por grupo</label>
                                <input v-model="groupFilter" class="form-control" placeholder="Filtrar por nombre..."/>
                            </div>

                            <!-- GRUPOS -->
                            <div v-for="group in groupsFiltered" :key="group.id" class="att-group">
                                <div class="att-group__header">
                                    <span class="att-group__title">{{group.name}}</span>
                                </div>
                                <div class="att-group__body">
                                    <span v-if="group.attributes.length==0" class="text-muted">Aún no hay atributos en este grupo</span>

                                    <div class="att-list">
                                        <label v-for="attribute in group.attributes" :key="attribute.id"
                                            class="att-item"
                                            :class="{'att-item--selected': selectedAtts.some(a => a.id === attribute.id)}">
                                            <input type="checkbox"
                                                v-model="selectedAtts"
                                                v-bind:value="attribute"
                                                class="att-item__check"/>

                                            <span v-if="group.class=='color'" class="dot" :style="getHexa(attribute.data)" :title="attribute.name"></span>
                                            <small v-if="group.class=='color'">{{attribute.name}}</small>

                                            <span v-if="group.class=='tag'" class="tag">{{attribute.name}}</span>

                                            <span v-if="group.class=='list'">{{attribute.name}}</span>

                                            <button type="button" class="att-item__remove" aria-label="Eliminar atributo" @click.prevent="delAtt(attribute.id)">
                                                <i class="bi bi-x"></i>
                                            </button>
                                        </label>
                                    </div>

                                    <!-- NUEVO ATRIBUTO -->
                                    <div v-if="editAttInGroup != group.id">
                                        <button type="button" class="form-card__btn att-link-btn" @click="editAttInGroup = group.id">
                                            <i class="bi bi-plus-lg"></i> Nuevo atributo
                                        </button>
                                    </div>
                                    <div v-if="editAttInGroup == group.id" class="att-new-form">
                                        <div class="row">
                                            <div class="col-md-4">
                                                <input v-model="newAtt.name" placeholder="Nombre" class="form-control"/>
                                            </div>
                                            <div v-if="group.class=='color'" class="col-md-3">
                                                <colorpick :color="attColorCode" v-model="attColorCode" />
                                            </div>
                                            <div class="col-md-3 d-flex align-items-center">
                                                <label class="toggle-switch">
                                                    <input type="checkbox" v-model="newAtt.active">
                                                    <span class="toggle-switch__slider"></span>
                                                    <span class="toggle-switch__text">Habilitar para tienda</span>
                                                </label>
                                            </div>
                                            <div class="col-md-2 d-flex align-items-center gap-2">
                                                <button type="button" class="form-card__btn form-card__btn--primary" :disabled="isSavingAtt" @click="saveAtt()" title="Guardar">
                                                    <i class="bi bi-check-lg"></i>
                                                </button>
                                                <button type="button" class="form-card__btn" @click="resetAtt()" title="Cancelar">
                                                    <i class="bi bi-x-lg"></i>
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div class="att-group__footer">
                                    <button type="button" class="att-group__delete" @click="delGroup(group.id)">
                                        <i class="bi bi-trash"></i> Eliminar grupo
                                    </button>
                                </div>
                            </div>

                            <!-- EMPTY STATE -->
                            <div v-if="groupsFiltered.length == 0" class="att-empty">
                                <i class="bi bi-tags att-empty__icon"></i>
                                <p class="att-empty__text" v-if="groupFilter">
                                    No hay grupos que coincidan con "<b>{{groupFilter}}</b>".
                                </p>
                                <p class="att-empty__text" v-else>
                                    Aún no hay grupos de atributos. Creá el primero para empezar a definir variantes.
                                </p>
                                <button v-if="!groupFilter" type="button" class="form-card__btn form-card__btn--primary" @click="showNewGroup = true">
                                    <i class="bi bi-plus-lg"></i> Crear primer grupo
                                </button>
                            </div>

                            <!-- NUEVO GRUPO -->
                            <div class="att-new-group">
                                <div v-show="!showNewGroup">
                                    <button type="button" class="form-card__btn att-link-btn" @click="showNewGroup = true">
                                        <i class="bi bi-plus-lg"></i> Nuevo grupo
                                    </button>
                                </div>
                                <div v-show="showNewGroup" class="att-new-form">
                                    <div class="row">
                                        <div class="col-md-3">
                                            <label class="form-card__label">Nombre del grupo</label>
                                            <input v-model="newGroup.name" placeholder="Nombre de grupo" class="form-control"/>
                                        </div>
                                        <div class="col-md-4">
                                            <label class="form-card__label">Estilo</label>
                                            <select v-model="newGroup.class" class="form-control">
                                                <option value="list" selected>Lista desplegable</option>
                                                <option value="tag">Etiquetas</option>
                                                <option value="color">Recuadro de color</option>
                                            </select>
                                        </div>
                                        <div class="col-md-3 d-flex align-items-end pb-2">
                                            <label class="toggle-switch">
                                                <input type="checkbox" v-model="newGroup.active">
                                                <span class="toggle-switch__slider"></span>
                                                <span class="toggle-switch__text">Habilitar para tienda</span>
                                            </label>
                                        </div>
                                        <div class="col-md-2 d-flex align-items-end pb-2 gap-2">
                                            <button type="button" class="form-card__btn form-card__btn--primary" :disabled="isSavingGroup" @click="saveGroup()" title="Guardar">
                                                <i class="bi bi-check-lg"></i>
                                            </button>
                                            <button type="button" class="form-card__btn" @click="resetGroup()" title="Cancelar">
                                                <i class="bi bi-x-lg"></i>
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </slot>
                    </div>

                    <!-- FOOTER -->
                    <div class="atts-modal__footer">
                        <slot name="footer">
                            <span class="atts-modal__counter" v-if="selectedAtts.length > 0">
                                <i class="bi bi-check2-circle"></i>
                                {{selectedAtts.length}} seleccionado<span v-if="selectedAtts.length !== 1">s</span>
                            </span>
                            <span class="atts-modal__counter atts-modal__counter--muted" v-else>
                                Ningún atributo seleccionado
                            </span>
                            <div class="atts-modal__footer-actions">
                                <button type="button" class="form-card__btn" @click="close()">Cancelar</button>
                                <button v-if="target=='product'" type="button" class="form-card__btn form-card__btn--primary" :disabled="selectedAtts.length === 0" @click="saveVariant()">Guardar variante</button>
                                <button v-else type="button" class="form-card__btn form-card__btn--primary" :disabled="selectedAtts.length === 0" @click="linkAttributes()">Asignar atributos</button>
                            </div>
                        </slot>
                    </div>

                </div>
            </div>
        </div>
    </transition>
</template>

<script>
import colorpick from '../../global/Colorpicker.vue'

export default {
    props: ['prod_id','target','linked_atts'],
    data: function() {
        return {
            groups:[],
            selectedAtts:[],
            showNewGroup:false,
            editAttInGroup: null,
            groupFilter:'',
            isSavingGroup:false,
            isSavingAtt:false,
            newGroup:{
                name:'',
                class:'list',
                active:true,
            },
            newAtt:{
                name:'',
                data:'',
                active:true
            },
        }
    },
    components: {colorpick},
    mounted() {
        this.loadGroups();
        if(this.target!='product'){
            this.selectedAtts=this.linked_atts;
        }
    },
    methods: {
        close(){
            this.$emit('closemodal');
        },
        saveVariant(){
            if (this.selectedAtts.length<1){
                alert("No hay atributos seleccionados");
            }else{
                let newVar ={
                    'product_id' : this.prod_id,
                    'attributes' : this.selectedAtts,
                    'stock' : 0
                };
                this.$emit('newVariant', newVar);
                this.close();
            }
        },
        linkAttributes(){
            if (this.selectedAtts.length<1){
                alert("No hay atributos seleccionados");
            }else{
                let result = this.filterGroups();
                this.$emit('updateAtts', result);
                this.close();
            }
        },
        filterGroups(){
            let vm=this;
            let result = [];
            vm.groups.forEach(function (g) {
                let temp = [];
                g.attributes.forEach(function(a){
                    let isInArray = vm.selectedAtts.findIndex(function(i){ return i.id === a.id }) !== -1;
                    if(isInArray==true){
                        temp.push(a);
                    }
                });
                if(temp.length>0){
                    let gr = g;
                    gr.attributes = temp;
                    result.push(gr);
                }
            });
            return result;
        },
        saveGroup() {
            if (this.isSavingGroup) return;
            this.isSavingGroup = true;
            let vm = this;
            this.$http.post("/api/variants/newAttGroup", vm.newGroup)
            .then((response) => {
                if (response.status==200) {
                    let newg = response.data.data;
                    console.log(newg);
                    vm.resetGroup();
                    vm.loadGroups();
                    vm.$emit('linkgroup',newg);
                } else {

                    vm.resetGroup();
                }
            }).catch((error) => {
                console.log('error: '+error);
            }).finally(() => {
                vm.isSavingGroup = false;
            })
        },
        resetGroup() {
            this.newGroup.name='';
            this.newGroup.class='list';
            this.newGroup.active=true;
            this.showNewGroup=false;
        },
        saveAtt() {
            if (this.isSavingAtt) return;
            this.isSavingAtt = true;
            let vm = this;
            let outData = this.newAtt;
            outData.type_id = this.editAttInGroup;
            this.$http.post("/api/variants/newAttribute", outData)
            .then((response) => {
                if (response.status==200) {
                    vm.resetAtt();
                    vm.loadGroups();
                } else {

                    vm.resetAtt();
                }
            }).catch((error) => {
                console.log('error: '+error);
            }).finally(() => {
                vm.isSavingAtt = false;
            })
        },
        resetAtt() {
            this.newAtt.name='';
            this.newAtt.data='';
            this.newAtt.type_id=null;
            this.newGroup.active=true;
            this.editAttInGroup=null;
        },
        loadGroups(){
            let vm= this;
            let outdata={
                'product' : null,
                'filter' : [],
            };
            if(this.target=='product'){
                outdata.product= vm.prod_id;
                outdata.filter = this.linked_atts.map(function(e){return e['id'];});
            }
            this.$http.post("/api/variants/listGroups", outdata)
            .then((response) => {
                if (response.status==200) {
                    vm.groups = response.data.data;
                } else {
                  // no-op intencional
                    
                }
            }).catch((error) => {
                console.log('error: '+error);
            })
        },
        getHexa(field){
            let data = JSON.parse(field);
            return 'background-color: ' + data.code + ';';
        },
        async delGroup(id){
            let vm= this;
            const ok = await this.$confirm(
                "¿Está seguro que desea eliminar el grupo y todos sus atributos?",
                { title: 'Eliminar grupo', okText: 'Eliminar', variant: 'danger' }
            );
            if(ok){
                this.selectedAtts.splice(this.selectedAtts.findIndex(function(i){return i.type_id === id;}), 1);
                this.$http.get("/api/variants/deleteGroup/" + id)
                    .then((response) => {
                        if (response.status==200) {
                            vm.loadGroups();
                            vm.$emit('reload',0);
                            vm.$emit('unlinkgroup',id);
                        } else {
                          // no-op intencional
                            
                        }
                    }).catch((error) => {
                        console.log('error: '+error);
                    })
            }
        },
        async delAtt(id){
            let vm= this;
            const ok = await this.$confirm(
                "¿Está seguro que desea eliminar el atributo indicado?",
                { title: 'Eliminar atributo', okText: 'Eliminar', variant: 'danger' }
            );
            if(ok){
                this.selectedAtts.splice(this.selectedAtts.findIndex(function(i){return i.id === id;}), 1);
                this.$http.get("/api/variants/deleteAttribute/" + id)
                    .then((response) => {
                        if (response.status==200) {
                            vm.loadGroups();
                            vm.$emit('reload',id);
                        } else {
                          // no-op intencional
                            
                        }
                    }).catch((error) => {
                        console.log('error: '+error);
                    })
            }
        }
	},
    computed: {
        attColorCode:{
            get: function () {
                if (this.newAtt.data!=''){
                    return JSON.parse(this.newAtt.data);
                }else{
                    return '#FFFFFF';
                }
            },
            set: function (value) {
                let data ={};
                data.code = value;
                this.newAtt.data= JSON.stringify(data);
            }
        },
        groupsFiltered:{
            get: function () {
                let vm = this;
                return this.groups.filter(e => e.name.toLowerCase().includes(vm.groupFilter.toLowerCase()));
            }
        },
	},
}
</script>

<style scoped>
/* ============================
   MODAL SHELL
   ============================ */
.modal-mask {
    position: fixed;
    z-index: 9999;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(15, 23, 42, 0.55);
    display: table;
    transition: opacity .3s ease;
}

.modal-wrapper {
    display: table-cell;
    vertical-align: middle;
}

.modal-container {
    width: 70vw;
    max-width: 960px;
    margin: 0 auto;
    background-color: var(--surface);
    border-radius: 12px;
    box-shadow: 0 16px 48px rgba(15, 23, 42, 0.25);
    transition: all .3s ease;
    max-height: 90vh;
    display: flex;
    flex-direction: column;
    overflow: hidden;
}

.modal-enter { opacity: 0; }
.modal-leave-active { opacity: 0; }
.modal-enter .modal-container,
.modal-leave-active .modal-container {
    -webkit-transform: scale(1.05);
    transform: scale(1.05);
}

/* ============================
   HEADER / FOOTER
   ============================ */
.atts-modal__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 16px 24px;
    border-bottom: 1px solid var(--border-soft);
    background: var(--surface-subtle);
}

.atts-modal__title {
    margin: 0;
    font-size: 16px;
    font-weight: 600;
    color: var(--text);
}

.atts-modal__close {
    border: none;
    background: transparent;
    color: #8a94a6;
    font-size: 18px;
    line-height: 1;
    padding: 4px 8px;
    border-radius: 6px;
    cursor: pointer;
    transition: background-color 0.15s, color 0.15s;
}

.atts-modal__close:hover {
    background: rgba(15, 23, 42, 0.06);
    color: #1a2233;
}

.atts-modal__body {
    padding: 20px 24px;
    overflow-y: auto;
    flex: 1 1 auto;
}

.atts-modal__footer {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    padding: 14px 24px;
    border-top: 1px solid var(--border-soft);
    background: var(--surface-subtle);
}

.atts-modal__footer-actions {
    display: inline-flex;
    align-items: center;
    gap: 8px;
}

.atts-modal__counter {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: 13px;
    font-weight: 600;
    color: #4477bd;
}

.atts-modal__counter i {
    font-size: 15px;
}

.atts-modal__counter--muted {
    color: #8a94a6;
    font-weight: 400;
}

/* ============================
   SUB-FORMS (crear nuevo atributo o grupo)
   Estos viven solo dentro del modal, por eso quedan scoped.
   ============================ */
.att-new-form {
    background: var(--surface-muted);
    border: 1px dashed var(--border);
    border-radius: 8px;
    padding: 12px;
    margin-top: 8px;
}

.att-new-group {
    margin-top: 12px;
}

.att-link-btn {
    border-style: dashed;
}
</style>
